import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	CanActivateChild,
	Router,
	RouterStateSnapshot,
} from '@angular/router';
import {
	catchError,
	concatMap,
	delay,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs';

// --- Facades ---
import { AuthFacade } from '@facades/auth.facade';
import { GuestClientsFacade } from '@facades/guest-clients.facade';
import { MenuLayoutFacade } from '@facades/menu-layout.facade';
import { ParametersFacade } from '@facades/parameters.facade';

// --- Services ---
import { LocalStorageService } from '@services/local-storage.service';

@Injectable({
	providedIn: 'root',
})
export class GuestGuard implements CanActivate, CanActivateChild {
	private isRequestingGuestToken = false;

	constructor(
		private _router: Router,
		private authFacade: AuthFacade,
		private guestClients: GuestClientsFacade,
		private localStorageService: LocalStorageService,
		private menuLayoutFacade: MenuLayoutFacade,
		private parametersFacade: ParametersFacade
	) {}

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<any> {
		return this.authFacade.authState$.pipe(
			switchMap(({ user }) => {
				if (user) return of(true);

				const guestToken = this.localStorageService.get('guestToken');
				if (guestToken) {
					return this.guestClients.getSettings().pipe(
						shareReplay(1),
						concatMap(parameters => {
							this.parametersFacade.setParameters(parameters);
							return this.ensureMenuLoaded();
						})
					);
				}

				if (!this.isRequestingGuestToken) {
					this.isRequestingGuestToken = true;

					return this.authFacade.guestToken(location.host).pipe(
						switchMap(() => {
							this.isRequestingGuestToken = false;
							return this.guestClients.getSettings();
						}),
						concatMap(parameters => {
							this.parametersFacade.setParameters(parameters);
							return this.ensureMenuLoaded();
						}),
						catchError(() => {
							this.isRequestingGuestToken = false;
							return of(false);
						})
					);
				}

				return of(false);
			})
		);
	}

	canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		return this.canActivate(route, state);
	}

	private ensureMenuLoaded() {
		return this.menuLayoutFacade.getMenuLayout().pipe(
			take(1),
			map(() => void 0),
			delay(100),
			map(() => this.parametersFacade.parameters.allowGuestUser || false),
			tap(allow => !allow && this._router.navigate(['/auth']))
		);
	}
}
